import { useState, useCallback, useMemo } from "react";
import { message, Row, Col, Input } from "antd";
import { Form, Typography } from "@dreambigger/design-system/src/components";
import {
  ResponseFields,
  CIFBusinessLocatorStepAssets,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { useApplication } from "../api";
import { useAuthToken, useSegment } from "@dreambigger/shared/src/hooks";
import { prefixFieldsWithSlug } from "../utils/prefixStepSlug";
import { changeStepForward } from "../utils/changeStepForward";
import { api, CoreBankingApi } from "@dreambigger/shared/src/api/acquire";

const tinKey = "tin";
const businessIdKey = "businessId";

const { Text } = Typography;

const stripDashes = (value: string) => {
  return value.replace(/-/g, "");
};

const CIFBusinessLocator = ({ flow, step, brand, progress }: StepProps) => {
  const [disabled, setDisabled] = useState(true);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [form] = Form.useForm();
  const segment = useSegment();

  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  const tokenHelper = useAuthToken(flow.financialInstitution.id);

  const { slug, type } = step;
  const {
    coreBankingConfigurationUUID,
    tinTitle,
    tinPlaceholder,
    verifyFailedMessage,
    nextSlug,
  } = step.assets as CIFBusinessLocatorStepAssets;

  const handleInput = (
    _changedValues: ResponseFields,
    values: ResponseFields
  ) => {
    setDisabled(!values[tinKey]);
  };

  const nextStep = useCallback(
    (inputValues: any) => {
      changeStepForward(nextSlug, inputValues);
    },
    [nextSlug]
  );

  const handleNext = useCallback(
    (values: any) => {
      if (!application) {
        message.error("Unable to find application");
        return;
      }
      if (!coreBankingConfigurationUUID) {
        message.error("Missing configuration");
        return;
      }

      setProcessing(true);

      const coreApi = new CoreBankingApi(api, tokenHelper);
      coreApi
        .locateCIF({
          coreBankingConfigurationUUID,
          tin: stripDashes(values[tinKey]),
        })
        .then(({ data }) => {
          const response: ResponseFields = {
            [tinKey]: stripDashes(values[tinKey]),
            [businessIdKey]: data.id,
          };

          applicationHelper
            .update(application.id, {
              slug,
              type,
              fields: response,
            })
            .then((saved) => {
              //don't move forward if the response is not saved
              if (!saved) {
                return;
              }
              nextStep(values);
            })
            .finally(() => {
              setProcessing(false);

              // Prefix step slug followed by double underscore infront of fieldName
              const prefixedProperties = prefixFieldsWithSlug({
                fields: response,
                slug,
              });

              // Track submission in segment
              segment.track({
                action: "Button Click",
                label: `Submit - ${slug}`,
                properties: prefixedProperties,
              });
            });
        })
        .catch(() => {
          setVerifyFailed(true);
          setProcessing(false);
        });
    },
    [application, step]
  );

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    handleInput,
    handleNext,
    disabled,
    processing,
  };

  return (
    <StepWrapper {...wrapperProps}>
      <Row>
        <Col xs={24} md={16}>
          <label>{tinTitle}</label>
          <Form.Item
            name={tinKey}
            valuePropName={"value"}
            validateTrigger="onBlur"
          >
            <Input.Password
              minLength={9}
              maxLength={11}
              placeholder={tinPlaceholder || "Enter Business TIN"}
              className="ao-bl-input"
              onFocus={() => setVerifyFailed(false)}
              onChange={(e) => {
                // Filter out non-numeric and non-dash characters
                const filteredValue = e.target.value.replace(/[^0-9-]/g, "");
                // Update the form field with filtered value
                form.setFieldsValue({ [tinKey]: filteredValue });
              }}
            />
          </Form.Item>

          {verifyFailed && (
            <Text className="warning">
              {verifyFailedMessage ||
                "We couldn't locate your business. Please try again."}
            </Text>
          )}
        </Col>
      </Row>
    </StepWrapper>
  );
};

export default CIFBusinessLocator;
